import type { ConversationTurn, QuestionResult, QuizPhase, StreamingSegment } from './useQuizSession'
import type { QuestionWithContext, AnswerResult, ConfidenceLevel } from '../types/models'

export type Action =
  | { type: 'SET_PHASE'; payload: QuizPhase }
  | { type: 'SET_DRAFT'; payload: string }
  | { type: 'SET_STREAMING'; payload: string | null }
  | { type: 'APPEND_STREAMING'; payload: StreamingSegment }
  | { type: 'SET_RETRYING'; payload: boolean }
  | { type: 'CONVERSATION_STARTED'; payload: { sessionId: string; jitQuestion: QuestionWithContext } }
  | { type: 'ANSWER_COMPLETE'; payload: { result: AnswerResult; confidenceLabel?: string } }
  | { type: 'SET_CONFIDENCE'; payload: ConfidenceLevel }
  | { type: 'SET_CONVERSATION_ERROR'; payload: string }
  | { type: 'CLEAR_CONVERSATION_ERROR' }

export function toUserMessage(raw: string): string {
  if (raw === 'HTTP 400') return 'HTTP_400'
  if (raw === 'HTTP 402') return 'HTTP_402'
  if (raw === 'HTTP 401' || raw === 'HTTP 403') return 'HTTP_401'
  return 'network'
}

export function reducer(state: QuizPhase, action: Action): QuizPhase {
  switch (action.type) {
    case 'SET_PHASE':
      return action.payload

    case 'SET_DRAFT':
      if (state.phase !== 'conversation') return state
      return { ...state, draftAnswer: action.payload }

    case 'SET_STREAMING':
      if (state.phase !== 'conversation') return state
      return { ...state, streamingText: action.payload, streamingSegments: [], isRetrying: false, conversationError: null }

    case 'SET_RETRYING':
      if (state.phase !== 'conversation') return state
      return { ...state, isRetrying: action.payload }

    case 'SET_CONVERSATION_ERROR':
      if (state.phase !== 'conversation') return state
      return { ...state, streamingText: null, streamingSegments: [], conversationError: action.payload }

    case 'CLEAR_CONVERSATION_ERROR':
      if (state.phase !== 'conversation') return state
      return { ...state, conversationError: null }

    case 'APPEND_STREAMING': {
      if (state.phase !== 'conversation') return state
      const { kind, text } = action.payload
      // `?? []` is needed: sessions persisted before this field existed restore without it.
      const prevSegments = state.streamingSegments ?? []
      const last = prevSegments[prevSegments.length - 1]
      const streamingSegments = last && last.kind === kind
        ? [...prevSegments.slice(0, -1), { kind, text: last.text + text }]
        : [...prevSegments, { kind, text }]
      return {
        ...state,
        streamingSegments,
        streamingText: streamingSegments.map(seg => seg.text).join('\n\n'),
        isRetrying: false,
      }
    }

    case 'CONVERSATION_STARTED':
      if (state.phase !== 'conversation') return state
      return { ...state, conversationSessionId: action.payload.sessionId, jitQuestion: action.payload.jitQuestion, isStartingConversation: false }

    case 'ANSWER_COMPLETE': {
      if (state.phase !== 'conversation') return state
      const { result, confidenceLabel } = action.payload
      const newHistory: ConversationTurn[] = [
        ...state.conversationHistory,
        { userAnswer: state.draftAnswer, result, confidenceLabel },
      ]
      const isCorrect = result.isCorrect ?? (result.score >= 50)
      const concept = state.jitQuestion ?? state.session.concepts[state.currentIndex]
      const newQuestionResults = result.conversationEnded
        ? [
            ...state.questionResults,
            {
              questionText: concept.question.questionText,
              specificTopic: concept.question.specificTopic,
              bookTitle: state.session.concepts[state.currentIndex].bookTitle,
              score: result.score,
              isCorrect,
              beforeStage: concept.growthStage,
              afterStage: result.growthStage ?? null,
              nextReviewInDays: result.nextReviewIn,
              sourceQuote: result.sourceQuote ?? null,
              sourceHighlight: result.sourceHighlight ?? null,
              expired: result.expired ?? false,
              newMilestones: result.newMilestones ?? [],
            },
          ]
        : state.questionResults
      return {
        ...state,
        conversationHistory: newHistory,
        streamingText: null,
        streamingSegments: [],
        isRetrying: false,
        draftAnswer: '',
        conversationEnded: result.conversationEnded,
        correctCount: result.conversationEnded && isCorrect ? state.correctCount + 1 : state.correctCount,
        questionResults: newQuestionResults,
      }
    }

    case 'SET_CONFIDENCE':
      if (state.phase !== 'conversation') return state
      return { ...state, confidence: action.payload }

    default:
      return state
  }
}


// Expired results carry placeholder scores, so they are dropped here only.
export function attemptedCount(totalCount: number, questionResults: QuestionResult[]): number {
  return Math.max(0, totalCount - questionResults.filter(r => r.expired).length)
}

// Session-scoped keys are not in getStats(), so they come from the answers.
export function mergeMilestones(
  perAnswer: string[][],
  fromStats: string[],
): string[] {
  return [...new Set([...perAnswer.flat(), ...fromStats])]
}
